import { forwardRef, type HTMLAttributes, useCallback } from 'react';

import { RippleRoot } from '../../../atoms';
import { cn } from '../../../utils';
import { useVirtualMultiselectItem } from '../contexts/item.context';
import { useVirtualMultiselectRoot } from '../contexts/root.context';
import { useVirtualMultiselectWindow } from '../contexts/window.context';

type VirtualMultiselectItemProps = HTMLAttributes<HTMLDivElement>;

export const VirtualMultiselectItem = forwardRef<
  HTMLDivElement,
  VirtualMultiselectItemProps
>(({ children, className, onClick, ...props }, ref) => {
  const { value, onValueChange } = useVirtualMultiselectRoot();
  const { id, virtualItem, disabled, selected } = useVirtualMultiselectItem();
  const { measureElement } = useVirtualMultiselectWindow();

  const toggleItem = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (selected) {
        onValueChange(value.filter((valueId) => valueId !== id));
      } else {
        onValueChange([...value, id]);
      }
      onClick?.(e);
    },
    [value, id, selected, onClick, onValueChange],
  );

  const combineRefs = useCallback(
    (el: HTMLDivElement) => {
      if (ref) {
        if (typeof ref === 'function') {
          ref(el);
        } else {
          ref.current = el;
        }
      }
      measureElement(el);
    },
    [ref, measureElement],
  );

  return (
    <div
      ref={combineRefs}
      data-index={virtualItem.index}
      data-selected={selected}
      data-disabled={disabled}
      className={cn(
        'flex flex-row px-3 py-2 w-full items-center gap-2',
        'cursor-pointer transition-colors data-[selected=true]:bg-primary/10 hover:bg-secondary',
        'data-[disabled=true]:bg-muted data-[disabled=true]:text-muted-foreground data-[disabled=true]:cursor-not-allowed data-[disabled=true]:pointer-events-none',
        className,
      )}
      role={'button'}
      style={{
        position: 'absolute',
        top: virtualItem.start,
      }}
      onClick={toggleItem}
      {...props}
    >
      {children}
      <RippleRoot className="bg-primary" />
    </div>
  );
});
VirtualMultiselectItem.displayName = 'VirtualMultiselectItem';
